import { Component, ViewChild, OnInit, OnDestroy, inject, ElementRef, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, take } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';

import { AddEditInsurancePage } from 'src/pages/insurance/add-edit-insurance/add-edit-insurance';
import { SteadilyQuotePage, SteadilyQuoteParams } from 'src/pages/insurance/steadily-quote/steadily-quote';

import { PropertyInsuranceService, PropertyInsurance, COVERAGE_TYPE_LABELS } from 'src/shared/services/property-insurance.service';
import { InsuranceInsightsService, EstimatesByAddress } from 'src/shared/services/insurance-insights.service';
import { SteadilyService } from 'src/shared/services/steadily.service';
import { Client } from 'src/providers/client/client';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { AddressModel } from 'src/models/address.model';
import {
  InsuranceInsight,
  InsuranceInsightAction,
  InsurancePortfolioSummary,
  PolicyStatus,
  PolicyStatusKind,
} from 'src/models/insurance-insight.model';
import { SteadilyEstimate } from 'src/models/steadily.model';

import { MatTooltipModule } from '@angular/material/tooltip';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import {
  InsurancePropertyCardsComponent,
  PropertyCardChip,
  PropertyCardEvent,
  PropertyCardPolicyView,
  PropertyCardSeverity,
  PropertyCardTone,
  PropertyCardView,
} from './insurance-property-cards/insurance-property-cards.component';

const DISMISSED_INSIGHTS_KEY = 'dismissed-insurance-insights';

/**
 * How many Steadily estimates to fetch up front. The rest are lazy-loaded as their
 * property cards scroll into view, so opening the page no longer fires one request per
 * property. Every card still renders immediately (mock pricing backs the not-yet-loaded
 * ones) and refines to the real estimate once its request lands.
 */
const INITIAL_ESTIMATE_BATCH = 8;

/**
 * Loads the customer's real policies (via `/address-insurances`) and real Steadily
 * estimates (via the proxy in gotidy PR #8538). Flip to true to render the hard-coded
 * demo portfolio that exercises every card state instead.
 */
const USE_MOCKUP_DEMO_DATA = false;

const POLICY_STATUS_TONES: Record<PolicyStatusKind, { bg: string; fg: string }> = {
  active: { bg: 'var(--tidy-success-bg)', fg: 'var(--tidy-success)' },
  expiring_soon: { bg: 'var(--tidy-warning-bg)', fg: 'var(--tidy-warning)' },
  pending_cancellation: { bg: 'var(--tidy-error-bg)', fg: 'var(--tidy-error)' },
  lapsed: { bg: 'var(--tidy-error-bg)', fg: 'var(--tidy-error)' },
  unverified: { bg: 'var(--tidy-bg-input)', fg: 'var(--tidy-text-light)' },
};

const POLICY_STATUS_CHIP_TONES: Record<PolicyStatusKind, PropertyCardTone> = {
  active: 'success',
  expiring_soon: 'warning',
  pending_cancellation: 'error',
  lapsed: 'error',
  unverified: 'neutral',
};

const CARD_SEVERITY_RANK: Record<PropertyCardSeverity, number> = {
  critical: 0,
  warning: 1,
  opportunity: 2,
  ok: 3,
};

interface HeroStat {
  value: string;
  label: string;
  tone: 'neutral' | 'warning' | 'success';
}

@Component({
  selector: 'insurance-content',
  templateUrl: './insurance-content.component.html',
  styleUrls: ['./insurance-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    ReactiveFormsModule,
    MatTooltipModule,
    TidyUiComponentsModule,
    SharedModule,
    InsurancePropertyCardsComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class InsuranceContentComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private navCtrl = inject(CustomNavController);
  private propertyInsuranceService = inject(PropertyInsuranceService);
  private rightSidePanelService = inject(RightSidePanelService);
  private insuranceInsightsService = inject(InsuranceInsightsService);
  private steadilyService = inject(SteadilyService);
  private client = inject(Client);
  private storage = inject(TidyStorage);
  private util = inject(Util);

  readonly windowService = inject(WindowService);
  private aboutThisPageService = inject(AboutThisPageService);
  private demoBlock = inject(DemoBlockService);

  insurances: PropertyInsurance[] = [];
  headers: string[] = [];
  rows: any[] = [];
  isDataLoaded = false;
  errorMessage: string;
  // Set when the policies request fails: the page then shows only the error, with no totals or
  // empty-state copy that would read as "you have no insurance".
  policiesLoadFailed = false;

  summary: InsurancePortfolioSummary | null = null;
  insights: InsuranceInsight[] = [];
  policyStatuses: Record<number, PolicyStatus> = {};
  addresses: AddressModel[] = [];
  private estimates: EstimatesByAddress = new Map();
  // Addresses whose estimate request has already been attempted (fulfilled or failed) and
  // those currently in flight, so a card scrolling in and out of view fetches at most once.
  // In-flight requests are kept so a quote click can wait for the one already running.
  private estimatesFetched = new Set<number>();
  private estimatesInflight = new Map<number, Promise<void>>();
  private estimateRebuildTimer: ReturnType<typeof setTimeout> | null = null;
  private dismissedInsightIds: string[] = [];
  insuranceCards: {
    insurance: PropertyInsurance;
    statusLabel: string;
    statusDetail: string;
    statusBg: string;
    statusFg: string;
  }[] = [];

  heroStats: HeroStat[] = [];
  propertyCards: PropertyCardView[] = [];
  filteredCards: PropertyCardView[] = [];
  emptyStateMessage = this.getEmptyStateMessage();
  searchForm = this.fb.nonNullable.group({ search: '' });

  @ViewChild('tidyTable') tidyTable: any;

  private panelClosedSubscription$?: Subscription;
  showAboutThisPage = false;
  overlayTop: string;
  private _insuranceHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  @ViewChild('insuranceHeader', { read: ElementRef }) set insuranceHeader(el: ElementRef) {
    this._insuranceHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }

  boundAddInsurance = this.addInsurance.bind(this);

  async ngOnInit() {
    this.searchForm.controls.search.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.applySearch());
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('insurance');
    if (this.showAboutThisPage) {
      await this.loadMockData();
      return;
    }
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load insurance';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  /** A property card scrolled into view — make sure its real Steadily estimate is loaded. */
  onCardVisible(addressId: number): void {
    this.ensureEstimateForAddress(addressId);
  }

  /** Resolves once this address's estimate request settles; `retryFailed` re-asks after a failure. */
  private async ensureEstimateForAddress(addressId: number, retryFailed = false): Promise<void> {
    // 0 is the synthetic id of the "no property" bucket — there's nothing to price.
    if (!addressId) { return; }
    if (USE_MOCKUP_DEMO_DATA || this.showAboutThisPage) { return; }
    const inflight = this.estimatesInflight.get(addressId);
    if (inflight) { return inflight; }
    if (this.estimatesFetched.has(addressId) && !retryFailed) { return; }
    const request = this.fetchEstimate(addressId);
    this.estimatesInflight.set(addressId, request);
    return request;
  }

  private async fetchEstimate(addressId: number): Promise<void> {
    try {
      const estimate = await this.steadilyService.getEstimate(addressId);
      if (estimate) {
        this.estimates.set(addressId, estimate);
        this.scheduleEstimateRebuild();
      }
    } catch {
      // A failed lazy fetch just leaves this card on mock pricing, same as the initial batch.
    } finally {
      this.estimatesInflight.delete(addressId);
      this.estimatesFetched.add(addressId);
    }
  }

  /** Coalesce a burst of newly-arrived estimates into a single insights/cards rebuild. */
  private scheduleEstimateRebuild(): void {
    if (this.estimateRebuildTimer !== null) { return; }
    this.estimateRebuildTimer = setTimeout(async () => {
      this.estimateRebuildTimer = null;
      await this.buildInsights(this.insurances, this.addresses);
      this.buildHeroStats();
      this.buildPropertyCards();
      this.cdr.markForCheck();
    }, 200);
  }

  private async buildInsights(insurances: PropertyInsurance[], addresses: AddressModel[]): Promise<void> {
    this.addresses = addresses;
    this.dismissedInsightIds = await this.storage.retrieve(DISMISSED_INSIGHTS_KEY) || [];
    this.policyStatuses = this.insuranceInsightsService.buildPolicyStatuses(insurances);
    this.insights = this.insuranceInsightsService.buildInsights(insurances, addresses, new Date(), this.estimates)
      .filter(insight => !this.dismissedInsightIds.includes(insight.id));
    this.summary = this.insuranceInsightsService.buildSummary(insurances, addresses, this.insights);
  }

  /** Offsets a date from today (local) so the preview always demonstrates the renewal states. */
  private relativeDate(daysFromToday: number): string {
    const date = new Date();
    date.setDate(date.getDate() + daysFromToday);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /**
   * Demo portfolio tuned against SteadilyMockService's pricing formula so every
   * card state appears exactly once: savings (123 Main St), expiring soon
   * (Mountain Retreat), uninsured (Beach Bungalow), expired + wrong coverage type
   * (Cozy Cottage), underinsured (Desert Oasis), healthy multi-policy (Lakeside Lodge).
   */
  private buildDemoDataset(): { addresses: AddressModel[]; insurances: PropertyInsurance[] } {
    const addresses: AddressModel[] = [
      { id: 1, address_name: '123 Main St', address1: '123 Main St', city: 'Tolleson', state: 'AZ', living_area_sqft: 1561, year_built: 2010, property_type: 'single_family', color: '#679BF8' },
      { id: 2, address_name: 'Mountain Retreat', address1: '55 Pine Ridge Rd', city: 'Breckenridge', state: 'CO', living_area_sqft: 2200, year_built: 1994, property_type: 'single_family', color: '#72CB9B' },
      { id: 3, address_name: 'Beach Bungalow', address1: '740 Gulf Shore Dr', city: 'Destin', state: 'FL', living_area_sqft: 2140, year_built: 2004, property_type: 'single_family', color: '#E7796D' },
      { id: 4, address_name: 'Cozy Cottage', address1: '8 Elm Ct', city: 'Asheville', state: 'NC', living_area_sqft: 1180, year_built: 1958, property_type: 'single_family', color: '#EFCE60' },
      { id: 6, address_name: 'Desert Oasis', address1: '9012 E Cactus Rd', city: 'Scottsdale', state: 'AZ', living_area_sqft: 1980, year_built: 1998, property_type: 'single_family', color: '#9FC65B' },
      { id: 7, address_name: 'Lakeside Lodge', address1: '18 Shoreline Way', city: 'Truckee', state: 'CA', living_area_sqft: 2600, year_built: 2015, property_type: 'single_family', color: '#8B7BE8' },
    ] as AddressModel[];
    const insurances = [
      { id: 1, address: { id: 1, address_name: '123 Main St' }, provider_name: 'State Farm', coverage_type: 'landlord_rental_property', coverage_amount: 25000000, annual_premium_amount: 180000, deductible_amount: 100000, auto_renew: true, expiration_date: this.relativeDate(230), policy_number: 'SF-10234', agent_name: 'Dana Whitfield', agent_phone: '+16025550118' },
      { id: 2, address: { id: 2, address_name: 'Mountain Retreat' }, provider_name: 'Allstate', coverage_type: 'short_term_rental', coverage_amount: 40000000, annual_premium_amount: 320000, deductible_amount: 250000, auto_renew: false, expiration_date: this.relativeDate(23), policy_number: 'AL-55891', agent_name: 'Marcus Bell', agent_phone: '+19705550142' },
      { id: 4, address: { id: 4, address_name: 'Cozy Cottage' }, provider_name: 'USAA', coverage_type: 'flood', coverage_amount: 19500000, annual_premium_amount: 108000, deductible_amount: 100000, auto_renew: true, expiration_date: this.relativeDate(-6), policy_number: 'UA-33210', agent_name: 'Priya Raman', agent_phone: '+18285550173' },
      { id: 6, address: { id: 6, address_name: 'Desert Oasis' }, provider_name: 'State Farm', coverage_type: 'landlord_rental_property', coverage_amount: 21000000, annual_premium_amount: 154800, deductible_amount: 150000, auto_renew: true, expiration_date: this.relativeDate(300), policy_number: 'SF-10567', agent_name: 'Dana Whitfield', agent_phone: '+16025550118' },
      { id: 7, address: { id: 7, address_name: 'Lakeside Lodge' }, provider_name: 'Steadily', coverage_type: 'landlord_rental_property', coverage_amount: 43000000, annual_premium_amount: 186000, deductible_amount: 100000, auto_renew: true, expiration_date: this.relativeDate(320), policy_number: 'ST-88412', agent_name: '', agent_phone: '' },
      { id: 8, address: { id: 7, address_name: 'Lakeside Lodge' }, provider_name: 'Neptune Flood', coverage_type: 'flood', coverage_amount: 43000000, annual_premium_amount: 48000, deductible_amount: 125000, auto_renew: true, expiration_date: this.relativeDate(200), policy_number: 'NF-20931', agent_name: '', agent_phone: '' },
    ] as any[] as PropertyInsurance[];
    return { addresses, insurances };
  }

  private async loadDemoDataset(): Promise<void> {
    this.buildHeaders();
    const { addresses, insurances } = this.buildDemoDataset();
    this.insurances = insurances;
    await this.buildInsights(insurances, addresses);
    this.buildRows(insurances);
    this.buildCards(insurances);
    this.buildHeroStats();
    this.buildPropertyCards();
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  private async loadMockData() {
    await this.loadDemoDataset();
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._insuranceHeader) {return;}
      const top = this._insuranceHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
    this.panelClosedSubscription$?.unsubscribe();
    if (this.estimateRebuildTimer !== null) {
      clearTimeout(this.estimateRebuildTimer);
    }
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.aboutThisPageService.dismissAboutThisPage('insurance');
    this.insurances = [];
    this.rows = [];
    this.insuranceCards = [];
    this.propertyCards = [];
    this.filteredCards = [];
    this.isDataLoaded = false;
    this.cdr.markForCheck();
    this.loadPage();
  }

  async loadPage() {
    if (USE_MOCKUP_DEMO_DATA) {
      await this.loadDemoDataset();
      return;
    }
    this.buildHeaders();
    // Loaded independently: a failing address lookup must not blank the policy list.
    const [insurancesResult, addressesResult] = await Promise.allSettled([
      this.propertyInsuranceService.getPropertyInsurances(),
      this.client.getAddresses(),
    ]);
    const policiesFailed = insurancesResult.status === 'rejected';
    this.policiesLoadFailed = policiesFailed;
    this.insurances = insurancesResult.status === 'fulfilled' ? insurancesResult.value || [] : [];
    if (policiesFailed) {
      this.errorMessage = 'We could not load your insurance policies. Please try again.';
    } else {
      this.errorMessage = addressesResult.status === 'rejected'
        ? 'We could not load your properties, so this page may be incomplete.'
        : undefined;
    }
    // A failed policy load shows only the error: no property cards, no "No Policy" pills, and
    // no totals (see buildHeroStats and the template's empty state).
    const addresses = policiesFailed ? [] : this.withPolicyAddresses(
      addressesResult.status === 'fulfilled' ? addressesResult.value || [] : []
    );
    await this.buildInsights(this.insurances, addresses);
    this.buildRows(this.insurances);
    this.buildCards(this.insurances);
    this.buildHeroStats();
    this.buildPropertyCards();
    this.isDataLoaded = true;
    this.cdr.markForCheck();
    // Estimates only refine the cards (a missing one falls back to mock pricing), so they
    // load after the first paint rather than holding the whole page on the skeleton.
    this.loadInitialEstimates(addresses);
  }

  /**
   * Every property a card is built for: the customer's addresses, plus a stub for any policy
   * whose address didn't come back. A failed or stale address lookup must not make the page
   * look like the customer has no insurance at all.
   */
  private withPolicyAddresses(addresses: AddressModel[]): AddressModel[] {
    const knownIds = new Set(addresses.map(address => address.id));
    const orphans = new Map<number, AddressModel>();
    this.insurances.forEach(insurance => {
      const addressId = insurance.address?.id;
      if (!addressId || knownIds.has(addressId) || orphans.has(addressId)) { return; }
      orphans.set(addressId, { id: addressId, address_name: insurance.address.address_name } as AddressModel);
    });
    return [...addresses, ...orphans.values()];
  }

  /**
   * Fetches the first batch of Steadily estimates. The rest lazy-load as cards scroll into
   * view (onCardVisible), so entering the page no longer fires one request per property.
   */
  private async loadInitialEstimates(addresses: AddressModel[]): Promise<void> {
    const initialIds = addresses.slice(0, INITIAL_ESTIMATE_BATCH).map(address => address.id);
    if (initialIds.length === 0) { return; }
    const batch = this.steadilyService.getEstimatesForAddresses(initialIds).then(initialEstimates => {
      // Merged, not replaced: reloading the page must not drop estimates already lazy-loaded
      // for cards further down, which `estimatesFetched` would then never fetch again.
      initialEstimates.forEach((estimate, id) => this.estimates.set(id, estimate));
      if (initialEstimates.size > 0) { this.scheduleEstimateRebuild(); }
    }).finally(() => {
      initialIds.forEach(id => {
        this.estimatesInflight.delete(id);
        this.estimatesFetched.add(id);
      });
    });
    const settled = batch.catch(() => undefined);
    initialIds.forEach(id => this.estimatesInflight.set(id, settled));
    await batch;
  }

  buildHeaders(): void {
    this.headers = [
      'Property',
      'Provider',
      'Status',
      'Coverage Type',
      'Coverage',
      'Deductible',
      'Premium',
      'Expiration',
      'last-column'
    ];
  }

  private buildHeroStats(): void {
    // Totals built from zero loaded policies would tell an insured client they have none.
    if (!this.summary || this.policiesLoadFailed) {
      this.heroStats = [];
      return;
    }
    const uncovered = this.summary.propertiesTotal - this.summary.propertiesCovered;
    this.heroStats = [
      {
        value: `$${this.summary.totalAnnualPremium.toLocaleString('en-US')}/yr`,
        label: 'total premium',
        tone: 'neutral',
      },
      {
        value: `${this.summary.propertiesCovered} of ${this.summary.propertiesTotal}`,
        label: 'covered',
        tone: uncovered > 0 ? 'warning' : 'success',
      },
      {
        value: `${this.summary.needsAttentionCount}`,
        label: 'need attention',
        tone: this.summary.needsAttentionCount > 0 ? 'warning' : 'neutral',
      },
      {
        value: `~$${this.summary.potentialAnnualSavings.toLocaleString('en-US')}/yr`,
        label: 'potential savings',
        tone: 'success',
      },
    ];
  }

  private buildPropertyCards(): void {
    const datePipe = new ParseDateTimePipe();
    const policyInsights = new Map<number, InsuranceInsight[]>();
    const uninsuredByAddress = new Map<number, InsuranceInsight>();
    this.insights.forEach(insight => {
      if (insight.kind === 'uninsured' && insight.addressId) {
        uninsuredByAddress.set(insight.addressId, insight);
      } else if (insight.policyId) {
        const list = policyInsights.get(insight.policyId) || [];
        list.push(insight);
        policyInsights.set(insight.policyId, list);
      }
    });

    const cards: PropertyCardView[] = this.addresses.map(address => {
      const policies = this.insurances.filter(insurance => insurance.address?.id === address.id);
      const name = address.address_name || address.address1 || 'Unknown Property';
      const subtitle = [address.city, address.state].filter(Boolean).join(', ');
      const color = address.color || '#8790A0';
      // Mock pricing backs any address the proxy hasn't answered for (see resolveEstimate).
      const pricingIsSample = !this.estimates.has(address.id);

      if (policies.length === 0) {
        const insight = uninsuredByAddress.get(address.id);
        return {
          addressId: address.id,
          name,
          subtitle,
          color,
          pricingIsSample,
          severity: 'critical',
          pill: { label: 'No Policy', tone: 'error', detail: 'This property has no coverage tracked in TIDY.' },
          policies: [],
          // A concrete "$X/mo" is only shown when a real Steadily estimate backs it. On sample
          // pricing we never quote a number the customer could mistake for a real one.
          estimateLabel: !pricingIsSample && insight?.monthlyPremium
            ? `Get insured for as low as $${insight.monthlyPremium.toLocaleString('en-US')}/mo with Steadily`
            : 'Get insured with Steadily',
        } as PropertyCardView;
      }

      let totalSavings = 0;
      let hasLapsed = false;
      let hasExpiring = false;
      let hasUnderinsured = false;
      let hasMismatch = false;

      const policyViews: PropertyCardPolicyView[] = policies.map(policy => {
        const view = this.buildPolicyRow(policy, datePipe);
        const status = this.policyStatuses[policy.id];
        hasLapsed = hasLapsed || status?.kind === 'lapsed';
        hasExpiring = hasExpiring || status?.kind === 'expiring_soon';

        (policyInsights.get(policy.id) || []).forEach(insight => {
          if (insight.kind === 'underinsured') {
            hasUnderinsured = true;
            view.chips.push({ label: 'Underinsured', tone: 'warning', detail: insight.title });
          }
          if (insight.kind === 'coverage_mismatch') {
            hasMismatch = true;
            view.chips.push({ label: 'No Rental Coverage', tone: 'warning', detail: insight.detail });
          }
          // A savings claim compares the real premium against a Steadily quote; on sample
          // pricing that quote is fabricated, so never surface a "Save $X/yr" the customer
          // would read as real. Those cards keep the sample badge instead.
          if (insight.kind === 'overpaying' && insight.savingsAmount && !pricingIsSample) {
            totalSavings += insight.savingsAmount;
            view.savingsLabel = `Save ~$${insight.savingsAmount.toLocaleString('en-US')}/yr with Steadily`;
          }
        });
        return view;
      });

      let severity: PropertyCardSeverity = 'ok';
      let pill: PropertyCardChip = { label: 'Covered', tone: 'success' };
      if (hasLapsed) {
        severity = 'critical';
        pill = { label: 'Expired', tone: 'error' };
      } else if (hasExpiring) {
        severity = 'warning';
        pill = { label: 'Expires Soon', tone: 'warning' };
      } else if (hasUnderinsured) {
        severity = 'warning';
        pill = { label: 'Underinsured', tone: 'warning' };
      } else if (hasMismatch) {
        severity = 'warning';
        pill = { label: 'Check Coverage', tone: 'warning' };
      } else if (totalSavings > 0) {
        severity = 'opportunity';
        pill = { label: `Save ~$${totalSavings.toLocaleString('en-US')}/yr`, tone: 'success' };
      }

      return {
        addressId: address.id,
        name,
        subtitle,
        color,
        pricingIsSample,
        severity,
        pill,
        policies: policyViews,
      } as PropertyCardView;
    }).sort((a, b) =>
      CARD_SEVERITY_RANK[a.severity] - CARD_SEVERITY_RANK[b.severity] || a.name.localeCompare(b.name)
    );

    const unassignedCard = this.buildUnassignedCard(datePipe);
    if (unassignedCard) { cards.push(unassignedCard); }

    this.propertyCards = cards;
    this.applySearch();
  }

  /** Status, provenance and summary line for one policy row. */
  private buildPolicyRow(policy: PropertyInsurance, datePipe: ParseDateTimePipe): PropertyCardPolicyView {
    const status = this.policyStatuses[policy.id];
    const chips: PropertyCardChip[] = [];
    if (status) {
      chips.push({
        label: status.label,
        tone: POLICY_STATUS_CHIP_TONES[status.kind],
        detail: status.detail,
      });
    }
    // Rows the nightly Steadily sync created (gotidy PR #8539) rather than the customer.
    if (policy.source === 'steadily') {
      chips.push({
        label: 'Steadily',
        tone: 'success',
        detail: policy.steadily_status
          ? `Synced from your Steadily policy · ${policy.steadily_status}`
          : 'Synced automatically from your Steadily policy',
      });
    }

    const premiumLabel = policy.annual_premium_amount
      ? `$${Math.round(policy.annual_premium_amount / 100).toLocaleString('en-US')}/yr`
      : '';
    const expirationLabel = policy.expiration_date
      ? `${status?.kind === 'lapsed' ? 'Expired' : 'Expires'} ${datePipe.transform(policy.expiration_date, 'MMM d, yyyy')}`
      : 'No expiration date';
    return {
      insuranceId: policy.id,
      title: `${policy.provider_name || 'Unknown Provider'} · ${COVERAGE_TYPE_LABELS[policy.coverage_type] || policy.coverage_type || 'Policy'}`,
      metaLine: [premiumLabel, expirationLabel].filter(Boolean).join(' · '),
      chips,
    };
  }

  /**
   * `address_id` is nullable, so a policy can come back with no property attached. Those
   * match no card and would otherwise be invisible — bucket them instead of dropping them.
   */
  private buildUnassignedCard(datePipe: ParseDateTimePipe): PropertyCardView | null {
    const orphans = this.insurances.filter(insurance => !insurance.address?.id);
    if (orphans.length === 0) { return null; }
    return {
      addressId: 0,
      isUnassigned: true,
      name: 'Policies without a property',
      subtitle: '',
      color: '#8790A0',
      severity: 'warning',
      pill: {
        label: 'Needs A Property',
        tone: 'warning',
        detail: 'Open the policy and pick the property it covers.',
      },
      policies: orphans.map(policy => this.buildPolicyRow(policy, datePipe)),
    };
  }

  private applySearch(): void {
    const term = this.searchForm.controls.search.value.trim().toLowerCase();
    this.filteredCards = !term
      ? this.propertyCards
      : this.propertyCards.filter(card =>
          card.name.toLowerCase().includes(term)
          || card.subtitle.toLowerCase().includes(term)
          || card.policies.some(policy => policy.title.toLowerCase().includes(term))
        );
    this.emptyStateMessage = term ? 'No properties match your search.' : this.getEmptyStateMessage();
    this.cdr.markForCheck();
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Provider cell carries the agent name underneath, which was captured but never shown. */
  private buildProviderCell(insurance: PropertyInsurance): string {
    const provider = this.escapeHtml(insurance.provider_name || '');
    if (!insurance.agent_name) { return provider; }
    return `${provider}<div style="font-size:11px;color:var(--tidy-text-light);line-height:1.4">`
      + `${this.escapeHtml(insurance.agent_name)}</div>`;
  }

  /**
   * Status badges render through the table's `[innerHTML]` cell, so they use inline
   * styles on theme variables rather than a class from this component's stylesheet.
   */
  private buildStatusCell(insuranceId: number): string {
    const status = this.policyStatuses[insuranceId];
    if (!status) { return ''; }
    const tone = POLICY_STATUS_TONES[status.kind];
    return `<span style="display:inline-block;font-size:11px;font-weight:600;line-height:1.6;`
      + `padding:1px 8px;border-radius:9999px;white-space:nowrap;`
      + `background:${tone.bg};color:${tone.fg}" title="${status.detail}">${status.label}</span>`;
  }

  buildRows(insurances: PropertyInsurance[]): void {
    const datePipe = new ParseDateTimePipe();
    this.rows = insurances.map(insurance => ({
      id: insurance.id,
      'Property': {
        value: insurance.address?.address_name || 'Unknown Property',
        action: null,
      },
      'Provider': {
        value: this.buildProviderCell(insurance),
        sortValue: insurance.provider_name || '',
        action: null,
      },
      'Status': {
        value: this.buildStatusCell(insurance.id),
        sortValue: this.policyStatuses[insurance.id]?.label || '',
        action: null,
      },
      'Coverage Type': {
        value: COVERAGE_TYPE_LABELS[insurance.coverage_type] || insurance.coverage_type || '',
        action: null,
      },
      'Coverage': {
        value: insurance.coverage_amount !== null && insurance.coverage_amount !== undefined ? `$${(insurance.coverage_amount / 100).toLocaleString('en-US')}` : '',
        action: null,
      },
      'Deductible': {
        value: insurance.deductible_amount !== null && insurance.deductible_amount !== undefined ? `$${(insurance.deductible_amount / 100).toLocaleString('en-US')}` : '',
        action: null,
      },
      'Premium': {
        value: insurance.annual_premium_amount !== null && insurance.annual_premium_amount !== undefined ? `$${(insurance.annual_premium_amount / 100).toLocaleString('en-US')}/yr` : '',
        action: null,
      },
      'Expiration': {
        value: insurance.expiration_date ? datePipe.transform(insurance.expiration_date, 'MMM d, yyyy') : '',
        // ISO date so the column sorts chronologically, not "Apr" before "Jan".
        sortValue: insurance.expiration_date || '',
        action: null,
      },
      'last-column': {
        value: insurance.id,
        action: null,
        hasChevron: true,
      },
    }));
  }

  buildCards(insurances: PropertyInsurance[]): void {
    this.insuranceCards = insurances.map(insurance => {
      const status = this.policyStatuses[insurance.id];
      const tone = status ? POLICY_STATUS_TONES[status.kind] : null;
      return {
        insurance,
        statusLabel: status?.label || '',
        statusDetail: status?.detail || '',
        statusBg: tone?.bg || 'transparent',
        statusFg: tone?.fg || 'var(--tidy-text-light)',
      };
    });
  }

  async onCardAction(event: PropertyCardEvent): Promise<void> {
    if (this.showAboutThisPage) { return; }
    switch (event.action) {
      case 'get_quote': {
        await this.openQuotePage(event.addressId, event.insuranceId);
        return;
      }
      case 'add_policy': {
        this.goToPage('add-edit-insurance', { addressId: event.addressId }, AddEditInsurancePage);
        return;
      }
      case 'edit_policy': {
        const insurance = this.insurances.find(ins => ins.id === event.insuranceId);
        if (!insurance) { return; }
        this.goToPage('add-edit-insurance', { insurance }, AddEditInsurancePage);
        return;
      }
    }
  }

  async onInsightAction(event: { insight: InsuranceInsight; action: InsuranceInsightAction }): Promise<void> {
    if (this.showAboutThisPage) { return; }
    const { insight, action } = event;
    switch (action) {
      case 'get_quote': {
        if (!insight.estimate) { return; }
        const policy = insight.policyId
          ? this.insurances.find(ins => ins.id === insight.policyId)
          : undefined;
        await this.goToQuotePage(insight.propertyName, insight.addressId ?? policy?.address?.id, policy);
        return;
      }
      case 'add_policy': {
        const address = this.addresses.find(a => a.id === insight.addressId);
        this.goToPage('add-edit-insurance', { addressId: address?.id }, AddEditInsurancePage);
        return;
      }
      case 'edit_policy': {
        const insurance = this.insurances.find(ins => ins.id === insight.policyId);
        if (!insurance) { return; }
        this.goToPage('add-edit-insurance', { insurance }, AddEditInsurancePage);
        return;
      }
      case 'dismiss': {
        this.dismissedInsightIds = [...this.dismissedInsightIds, insight.id];
        await this.storage.save(DISMISSED_INSIGHTS_KEY, this.dismissedInsightIds);
        this.insights = this.insights.filter(item => item.id !== insight.id);
        this.summary = this.insuranceInsightsService.buildSummary(this.insurances, this.addresses, this.insights);
        this.cdr.markForCheck();
        return;
      }
    }
  }

  sortChanged(sort: any): void {
    this.rows.sort((a, b) => {
      const valA = a[sort.active]?.sortValue ?? a[sort.active]?.value ?? '';
      const valB = b[sort.active]?.sortValue ?? b[sort.active]?.value ?? '';

      if (sort.active === 'Coverage' || sort.active === 'Premium' || sort.active === 'Deductible') {
        const numA = parseFloat(String(valA).replace(/[$,/yr]/g, ''));
        const numB = parseFloat(String(valB).replace(/[$,/yr]/g, ''));
        return sort.direction === 'asc' ? numA - numB : numB - numA;
      }

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      if (strA < strB) {return sort.direction === 'asc' || sort.direction === '' ? -1 : 1;}
      if (strA > strB) {return sort.direction === 'asc' || sort.direction === '' ? 1 : -1;}
      return 0;
    });
  }

  onTableColumnClick(event: any): void {
    if (this.showAboutThisPage) {return;}
    if (this.demoBlock.block()) { return; }
    const foundInsurance = this.insurances.find(ins => ins.id === event.element.id);
    if (!foundInsurance) { return; }
    this.goToPage('add-edit-insurance', { insurance: foundInsurance }, AddEditInsurancePage);
  }

  addInsurance(): void {
    if (this.showAboutThisPage) {return;}
    if (this.demoBlock.block()) { return; }
    this.goToPage('add-edit-insurance', {}, AddEditInsurancePage);
  }

  /** Card CTAs carry only ids, so resolve the property and policy behind them. */
  private async openQuotePage(addressId: number, insuranceId?: number): Promise<void> {
    const policy = insuranceId ? this.insurances.find(ins => ins.id === insuranceId) : undefined;
    const address = this.addresses.find(item => item.id === addressId);
    const insight = this.insights.find(item => item.addressId === addressId);
    const propertyName = address?.address_name || address?.address1 || insight?.propertyName || 'Your Property';
    await this.goToQuotePage(propertyName, addressId, policy);
  }

  /**
   * Opens the quote page, which only ever shows a real Steadily estimate. A click that beats the
   * estimate waits for it, and retries one that failed. With still no price, say so: sample
   * pricing has no Steadily link of its own, and a made-up quote must not appear as Steadily's.
   */
  private async goToQuotePage(propertyName: string, addressId?: number, policy?: PropertyInsurance): Promise<void> {
    if (this.demoBlock.block()) { return; }
    const estimate = await this.loadRealEstimate(addressId);
    if (!estimate) {
      this.util.showError('Steadily could not give a quote for this property right now. Please try again later.');
      return;
    }
    const address = addressId ? this.addresses.find(item => item.id === addressId) : undefined;
    const addressLine = [address?.address1, address?.city, address?.state].filter(Boolean).join(', ');
    const params: SteadilyQuoteParams = {
      estimate,
      propertyName,
      addressLine,
      currentProvider: policy?.provider_name || '',
      currentAnnualPremium: policy?.annual_premium_amount ? policy.annual_premium_amount / 100 : 0,
    };
    this.goToPage('steadily-quote', params, SteadilyQuotePage);
  }

  private async loadRealEstimate(addressId?: number): Promise<SteadilyEstimate | undefined> {
    if (!addressId) { return undefined; }
    if (!this.estimates.has(addressId)) {
      // Started before the spinner, so an estimate that lands meanwhile isn't asked for again.
      const request = this.ensureEstimateForAddress(addressId, true);
      const loading = await this.util.showLoading();
      try {
        await request;
      } finally {
        loading?.dismiss();
      }
    }
    return this.estimates.get(addressId);
  }

  async goToPage(url: string, params: any, component: any): Promise<void> {
    if (this.demoBlock.block()) { return; }
    if (this.windowService.isDesktopRes) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        // Fire-and-forget (matches every other panel opener): awaiting these can throw
        // before the panel opens. Params reach the page race-free via `data` below;
        // storage is only the fallback for the back/reload flow.
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component, params);
      }
      this.panelClosedSubscription$?.unsubscribe();
      this.panelClosedSubscription$ = this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(async () => {
        const shouldReload = await this.storage.retrieve('shouldReloadPageAfterClosingSideRail');
        if (shouldReload) {
          await this.loadPage();
          await this.storage.delete('shouldReloadPageAfterClosingSideRail');
        }
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  getEmptyStateMessage(): string {
    return 'No insurance policies configured. Add insurance held by each property to track coverage and expiration dates.';
  }
}
